import type { subscriptions } from '../powersync/schemas/subscriptions.schema';

/**
 * The organization's plan, worked out from its synced `subscriptions` rows.
 *
 * Plain functions, so the rules can be tested without a database. `useOrgPlan` is the React side.
 *
 * The limits and entitlements below copy the feature table in MDC-4517. `useOrgPlan` replaces them
 * with the backend's (`withServerAllowance` in `serverPlan`) once `GET /organization/plan` answers;
 * the table covers what the backend doesn't serve, and the time offline or before it answers. Keep
 * the table in this file: a plan slug compared anywhere else is a gate the endpoint can't reach.
 */

export const PLAN_SLUG = {
  free: 'free',
  essentials: 'essentials',
  plus: 'plus',
  pro: 'pro',
} as const;

export type PlanSlug = (typeof PLAN_SLUG)[keyof typeof PLAN_SLUG];

/** What a plan counts. A limit of `null` is unlimited. */
export const ORG_LIMIT = {
  boats: 'boats',
  /** Members, the owner included. */
  users: 'users',
  storageGb: 'storageGb',
  aiQueriesPerMonth: 'aiQueriesPerMonth',
  theBoatDbCompares: 'theBoatDbCompares',
} as const;

export type OrgLimit = (typeof ORG_LIMIT)[keyof typeof ORG_LIMIT];

/**
 * What a plan switches on.
 *
 * MCP access isn't here: Free gets a limited version rather than none, and the assistant enforces
 * it, not the app. The features MDC-4517 lists as planned stay out until they ship.
 */
export const ORG_ENTITLEMENT = {
  customLocations: 'customLocations',
  customCategories: 'customCategories',
  customFields: 'customFields',
  theBoatDbApi: 'theBoatDbApi',
  prioritySupport: 'prioritySupport',
  commercialUse: 'commercialUse',
} as const;

export type OrgEntitlement = (typeof ORG_ENTITLEMENT)[keyof typeof ORG_ENTITLEMENT];

export type OrgLimits = Readonly<Record<OrgLimit, number | null>>;

type PlanAllowance = {
  limits: OrgLimits;
  entitlements: readonly OrgEntitlement[];
};

/**
 * The feature table in MDC-4517. Enterprise has no slug yet; its values are custom per contract and
 * will only ever come from the endpoint.
 */
const PLAN_ALLOWANCES: Readonly<Record<PlanSlug, PlanAllowance>> = {
  free: {
    limits: { boats: 1, users: 1, storageGb: 1, aiQueriesPerMonth: 5, theBoatDbCompares: 5 },
    entitlements: [],
  },
  essentials: {
    limits: { boats: 1, users: 1, storageGb: 5, aiQueriesPerMonth: 20, theBoatDbCompares: null },
    entitlements: [ORG_ENTITLEMENT.customLocations],
  },
  plus: {
    limits: { boats: 2, users: 5, storageGb: 15, aiQueriesPerMonth: 50, theBoatDbCompares: null },
    entitlements: [ORG_ENTITLEMENT.customLocations, ORG_ENTITLEMENT.customCategories],
  },
  pro: {
    limits: { boats: 5, users: 20, storageGb: 50, aiQueriesPerMonth: 100, theBoatDbCompares: null },
    entitlements: [
      ORG_ENTITLEMENT.customLocations,
      ORG_ENTITLEMENT.customCategories,
      ORG_ENTITLEMENT.customFields,
      ORG_ENTITLEMENT.theBoatDbApi,
      ORG_ENTITLEMENT.prioritySupport,
    ],
  },
};

/**
 * A plan this app doesn't know: a legacy tier, or a subscription with several items stored with no
 * plan at all. The backend leaves those accounts' member limit alone, so the app leaves them
 * ungated rather than lock out a paying customer. Their boats still follow the billed quantity.
 */
const UNKNOWN_PLAN_ALLOWANCE: PlanAllowance = {
  limits: { boats: null, users: null, storageGb: null, aiQueriesPerMonth: null, theBoatDbCompares: null },
  entitlements: Object.values(ORG_ENTITLEMENT),
};

export type SubscriptionRow = Pick<
  typeof subscriptions.$inferSelect,
  'id' | 'plan' | 'quantity' | 'stripe_status' | 'ends_at' | 'trial_ends_at' | 'next_billing_date'
>;

/**
 * The Stripe statuses that still give an organization its plan.
 *
 * The same three the backend's `Organization::memberAllowance()` counts. Past due keeps the plan
 * while Stripe retries the card. Dates are deliberately ignored: the legacy backend never clears
 * `ends_at` when a cancelled plan is renewed, so only Stripe's status takes a plan away.
 */
const OPEN_STRIPE_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing', 'past_due']);

/**
 * Whether a row still gives the organization its plan.
 *
 * `stripe_status` is never null on the server. A null here is a device that synced before the
 * stream started sending the column, and it counts as open: the same rule as a plan that hasn't
 * loaded, since reading every organization as Free would lock paying ones out.
 */
const isOpen = (row: SubscriptionRow): boolean =>
  row.stripe_status === null || OPEN_STRIPE_STATUSES.has(row.stripe_status);

const isPlanSlug = (plan: string | null): plan is PlanSlug =>
  plan !== null && Object.values<string>(PLAN_SLUG).includes(plan);

/**
 * Higher counts first. An unknown plan ranks above every known one because it is ungated, which is
 * also how the backend's member limit treats it.
 */
const PLAN_RANK: Readonly<Record<PlanSlug, number>> = { free: 0, essentials: 1, plus: 2, pro: 3 };
const UNKNOWN_PLAN_RANK = 4;

const rankOf = (row: SubscriptionRow): number => (isPlanSlug(row.plan) ? PLAN_RANK[row.plan] : UNKNOWN_PLAN_RANK);

/**
 * The subscription that decides the organization's plan: the highest one Stripe still has open.
 * `null` means the organization is on Free, whether it has a free row or no row at all.
 */
export const pickCountingSubscription = (rows: readonly SubscriptionRow[]): SubscriptionRow | null => {
  const open = rows.filter(isOpen);

  if (open.length === 0) {
    return null;
  }

  return open.reduce((best, row) => (rankOf(row) > rankOf(best) ? row : best));
};

export type OrgPlan = {
  /** `null` for a plan this app doesn't know. See `UNKNOWN_PLAN_ALLOWANCE`. */
  slug: PlanSlug | null;
  /** The Laravel id of the subscription that counts. `null` on Free with no paid row. */
  subscriptionId: string | null;
  /** Stripe's status for that subscription. `null` on Free with no paid row. */
  stripeStatus: string | null;
  /** The card failed and Stripe is retrying it. The plan still applies. */
  isPastDue: boolean;
  /** When the next invoice is due. `null` once renewal is cancelled. */
  renewsAt: string | null;
  /** When a cancelled plan stops. After that the organization moves to Free. */
  endsAt: string | null;
  trialEndsAt: string | null;
  limits: OrgLimits;
  entitlements: ReadonlySet<OrgEntitlement>;
};

const FREE_ALLOWANCE = PLAN_ALLOWANCES.free;

export const FREE_ORG_PLAN: OrgPlan = {
  slug: PLAN_SLUG.free,
  subscriptionId: null,
  stripeStatus: null,
  isPastDue: false,
  renewsAt: null,
  endsAt: null,
  trialEndsAt: null,
  limits: FREE_ALLOWANCE.limits,
  entitlements: new Set(FREE_ALLOWANCE.entitlements),
};

/**
 * How many boats a subscription covers: its plan's allowance, or the quantity it is billed for when
 * that is more. The quantity only exceeds 1 for an account still billed per boat (MDC-4516), which
 * keeps the boats it pays for. Mirrors `Subscription::boatAllowance()`.
 */
const boatLimitFor = (row: SubscriptionRow, planBoats: number | null): number => {
  const billed = Math.max(1, row.quantity ?? 1);

  return Math.max(planBoats ?? 1, billed);
};

export const resolveOrgPlan = (rows: readonly SubscriptionRow[]): OrgPlan => {
  const row = pickCountingSubscription(rows);

  if (!row) {
    return FREE_ORG_PLAN;
  }

  const slug = isPlanSlug(row.plan) ? row.plan : null;

  if (slug === PLAN_SLUG.free) {
    return { ...FREE_ORG_PLAN, subscriptionId: row.id, stripeStatus: row.stripe_status };
  }

  const allowance = slug ? PLAN_ALLOWANCES[slug] : UNKNOWN_PLAN_ALLOWANCE;

  return {
    slug,
    subscriptionId: row.id,
    stripeStatus: row.stripe_status,
    isPastDue: row.stripe_status === 'past_due',
    renewsAt: row.ends_at ? null : row.next_billing_date,
    endsAt: row.ends_at,
    trialEndsAt: row.trial_ends_at,
    limits: { ...allowance.limits, boats: boatLimitFor(row, allowance.limits.boats) },
    entitlements: new Set(allowance.entitlements),
  };
};

/** A known plan's limits and entitlements, from the same stopgap table `resolveOrgPlan` reads. */
export const planAllowance = (slug: PlanSlug): { limits: OrgLimits; entitlements: ReadonlySet<OrgEntitlement> } => ({
  limits: PLAN_ALLOWANCES[slug].limits,
  entitlements: new Set(PLAN_ALLOWANCES[slug].entitlements),
});

/** The known plans, cheapest first. */
export const PLANS_IN_ORDER: readonly PlanSlug[] = [
  PLAN_SLUG.free,
  PLAN_SLUG.essentials,
  PLAN_SLUG.plus,
  PLAN_SLUG.pro,
];

/**
 * Whether one more of `resource` fits: `used` is what the organization has now. Unlimited always
 * fits.
 */
export const hasRoomFor = (limit: number | null, used: number): boolean => limit === null || used < limit;
